<template>
  <div class="c-circle-select u-margin-bottom-xx-small">
    <div
      :name="ariaSelectId"
      class="c-circle-select__select"
      :value="value"
    >
      <div
        class="c-circle-select"
        :class="selectClass"
        :aria-label="`Select ${ariaValue}`"
      >
        <button
          v-for="(item, index) in computedOptions"
          :key="`${index}-${item.group}`"
          :hidden="index == 0"
          class="c-circle-select__option"
          :class="`${
            selected == item.value ? 'c-circle-select__option--active' : ''
          }`"
          :aria-label="`${ariaValue}: ${item.value} ${
            selected == item.value ? 'currently selected' : ''
          }`"
          :disabled="item.disabled && allowDisable"
          :title="(item.disabled && allowDisable) ? disableLabel : undefined"
          :style="circleStyle(item)"
          @click="handleChange(item)"
        >
          <slot v-if="hasSlot" />
          <div
            v-else-if="themeValue"
            class="c-circle-select__option-container"
          >
            <img
              :src="item.image"
              :alt="item.label"
              class="c-circle-select__option-image"
            >
            <img
              v-if="selected == item.value"
              class="c-circle-select__option-close"
              src="@/assets/icons/nav-close--black.svg"
              @click.stop="handleChange(null)"
            >
            <p class="t-paragraph c-circle-select__option-label">
              {{ item.label }}
            </p>
          </div>
          <template v-else>
            {{ item.value }}
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import get from "lodash/get";
const emptyValues = ["", NaN, null, undefined];
/*
 * welcome to 2006
 *
 * option groups can be specified by passing in an array of objects in this format:
 *
 * ```
 * {
 *   group: 'Group Label',
 *   options: [
 *     {key: 'Option label', value: 'Option value'} || 'Option value/label',
 *     // ...
 *   ]
 * }
 * ```
 */
export default {
  name: "Dropdown",
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    columns: {
      type: [Number, String],
      default: null,
    },
    placeholder: {
      type: [String, Boolean],
      default: "Select an Option",
    },
    options: {
      type: Array,
      required: true,
    },
    allowDisable: {
      type: Boolean,
      default: true
    },
    ariaValue: {
      type: String,
      default: 'Size'
    },
    themeValue: {
      type: Boolean,
      default: false
    },
    disableLabel: {
      type: String,
      default: 'Out of Stock'
    }
  },
  computed: {
    ariaSelectId() {
      return `${this._uid}-c-circle-select__select`;
    },
    placeholderOption() {
      const placeholder = this.placeholder;
      const value = this.value;
      return {
        label: placeholder || "(Please select one)",
        value: null,
        disabled: true,
        selected: emptyValues.includes(value),
      };
    },
    computedOptions() {
      const options = this.options;
      const placeholderOption = this.placeholderOption;
      return [placeholderOption, ...this.normalizeOptions(options)];
    },
    hasSlot() {
      return !!this.$slots.default;
    },
    selectClass() {
      let result = ['l-flex', 'l-flex--align-start', 'l-flex--justify-start']
      const columns = this.columns
      if(columns) {
        result.push('l-grid', `l-grid--${columns}-col`)
      }
      return result
    },
    selected() {
      return this.value
    }
  },
  methods: {
    normalizeOption(item) {
      const option = get(item, "label") ? item : { label: item, value: item };
      return {
        ...option,
        selected: `${option.value}` === `${this.value}`,
      };
    },
    normalizeOptions(options = []) {
      let result = [];
      for (let item of options) {
        if (item.group) {
          result.push({
            ...item,
            options: (item.options || []).map(this.normalizeOption),
          });
        } else {
          result.push(this.normalizeOption(item));
        }
      }
      return result;
    },
    circleStyle(item) {
      let result
      const themeValue = this.themeValue
      const font = item.label || ''
      if(item.style && font && !themeValue) {
        result = {fontFamily: `Enso ${font}`}
      } else {
        return item.style
      }
      return result
    },
    handleChange(event) {
      const data = event ? event.value : event;
      this.$emit("input", data);
    },
  },
};
</script>

<style lang="scss" scoped>
.c-circle-select {
  width: 100%;
  grid-row-gap: 0px;
  &__option {
    width: rem(45px);
    height: rem(45px);
    border-radius: 100%;
    margin: rem(10px) rem(20px) rem(10px) 0;
    background: transparent;
    @include themify($themes) {
      border: 1px solid themed("foreground", "select");
      &[disabled] {
        color: themed("foreground", "select");
        cursor: not-allowed;
      }
    }

    &:hover {
      cursor: pointer;
    }

    &--active {
      @include themify($themes) {
        background-color: themed("foreground", "select");
      }
    }
    &-container {
      position: relative;
    }
    &-image {
      max-height: rem(180px);
    }
    &-label {
      position: absolute;
      bottom: rem(-5px);
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
    }
    &-close {
      position: absolute;
      top: rem(10px);
      right: rem(10px);
    }
  }
}
</style>